import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {KnockoutFieldModel} from "../knockout-field/knockout-field.model";
import {KnockoutFieldComponent} from "../knockout-field/knockout-field.component";
import {QueryBuilderViewModel, QueryFiltersKoRenderInfoEventModel} from "./query-filters-ko-render-info-event.model";
import {DialogComponent} from '@syncfusion/ej2-angular-popups';
import {QueryFiltersSavedQueryModel} from "./query-filters-saved-query.model";
import {QueryFiltersService} from "./query-filters.service";
import {InternationalizationService} from "../../services/internationalization/internationalization.service";
import {Subscription} from "rxjs";
import {$translate} from "../../services/internationalization/localize.service";

@Component({
  selector: 'sb-query-filters',
  templateUrl: './query-filters.component.html',
  styleUrls: ['./query-filters.component.scss']
})
export class QueryFiltersComponent implements OnInit {
  @ViewChild('koFilter') koFilter: KnockoutFieldComponent;
  @ViewChild('SaveQueryDialog') public saveQueryDialog: DialogComponent;

  @Input()
  loadFilters: boolean = false;

  @Input()
  tableToQuery: string;

  @Input()
  showFilters: boolean = false;

  @Output("apply")
  applyQueryEmitter: EventEmitter<string> = new EventEmitter()

  @Output("save")
  saveQueryEmitter: EventEmitter<QueryFiltersSavedQueryModel> = new EventEmitter<QueryFiltersSavedQueryModel>();

  filterConfig: KnockoutFieldModel;
  private appliedQuery: string;
  private currentQuery: string;

  queryNameInputPlaceHolder: string = null;
  openSaveQueryDialogButtonLabel: string = null;
  saveDialogTitle: string = null;
  saveDialogMessage: string = null;
  saveDialogResetButtonLabel: string = null;
  saveDialogCancelButtonLabel: string = null;
  saveDialogSaveButtonLabel: string = null;

  savedQueryName: string = null;
  showSaveQueryButton: boolean = false;
  private queryBuilderViewModel: QueryBuilderViewModel;
  private localeChanged$: Subscription;


  constructor(private queryFiltersService: QueryFiltersService,
              private i18nService: InternationalizationService) {
  }

  ngOnInit(): void {
    this.initialiseFilters();
    this.initialiseTranslations();

    this.localeChanged$ = this.i18nService.localeChanged$.subscribe(async (locale: string) => {
      this.initialiseTranslations();
    });
  }

  public async applyQueryString(query: string) {
    if (query) {
      this.currentQuery = query;
      if (this.queryBuilderViewModel) {
        await this.queryBuilderViewModel.setAndApplyInitialQuery(JSON.parse(this.currentQuery));
      }
    }
  }

  public async clearQuery() {
    if (this.queryBuilderViewModel) {
      await this.queryBuilderViewModel.setAndApplyInitialQuery(this.queryFiltersService.QUERY_EMPTY_JSON);
    }
  }

  private initialiseFilters() {
    this.filterConfig = {
      name: 'String',
      datatype: 'knockout-string',
      availableValues: [],
      referenceTable: null,
      readonly: false,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null, // null | {value?: string, displayValue?: string };
        defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
        answer: {
          value: null,
          displayValue: null
        }, // null | {value?: string, displayValue?: string };
      },
      changed: null,
      requestId: null,
      table: 'SyntheticTable',
      renderer: 'condition',
      rendererProperties: {
        "dependent.table": this.tableToQuery || 'User', // Random table to start with if none is defined, will get changed later on.
        // Value is json query
        onApplyFilter: (value) => this.onApplyQuery(value),
        "update.model.on.change": true,
        showFooter: true,
        showApply: true,
        showCancel: false,
      },
      valueChangeCallback: (value) => this.onQueryChange(value),
    }
  }

  private onApplyQuery(value: any) {
    this.appliedQuery = JSON.stringify(value);

    this.filterConfig.data.answer.value = this.appliedQuery;
    this.filterConfig.data.answer.displayValue = this.appliedQuery;
    this.applyQueryEmitter.emit(`${this.appliedQuery}`)
  }

  async fieldRenderInfo(data: QueryFiltersKoRenderInfoEventModel) {
    if (data && data.queryBuilder && this.tableToQuery) {
      this.queryBuilderViewModel = data.queryBuilder;
      try {
        await data.queryBuilder.setTable(this.tableToQuery);
        if (this.currentQuery) {
          await this.applyQueryString(this.currentQuery);
        }
      } catch (ex) {
        console.error(`QueryFiltersComponent.fieldRenderInfo error setting table ${this.tableToQuery}`, ex);
      }
    }
  }

  onSaveQuery() {
    if (this.savedQueryName && this.queryFiltersService.isQueryNotEmpty(this.currentQuery)) {
      this.saveQueryEmitter.emit({name: this.savedQueryName, query: this.currentQuery});
      this.queryBuilderViewModel.applyQuery();
      this.hideSaveQueryModal();
    }
  }

  showSaveQueryModal = (): void => {
    this.saveQueryDialog.show();
  }

  hideSaveQueryModal = (): void => {
    this.savedQueryName = null;
    this.saveQueryDialog.hide();
  }





  onCancelSavedQuery() {
    this.hideSaveQueryModal();
  }

  private onQueryChange(value: string) {
    if (this.queryFiltersService.isQueryNotEmpty(value)) {
      this.currentQuery = value;
      this.showSaveQueryButton = true;
    } else {
      this.showSaveQueryButton = false;
    }
  }

  onQueryNameKeyDown(e: KeyboardEvent) {
    if (e.key == 'Enter') {
      this.onSaveQuery();
    }
  }

  async onResetQuery() {
    await this.clearQuery();
    this.hideSaveQueryModal();
  }

  private initialiseTranslations() {
    this.openSaveQueryDialogButtonLabel = $translate("ng.component.query-filters.open-save-query-dialog-button-label")`Save`;

    // Dialog
    this.queryNameInputPlaceHolder = $translate("ng.component.query-filters.query-name-placeholder")`Enter a name for your filter`;

    this.saveDialogTitle = $translate("ng.component.query-filters.save-query-dialog-title")`Save your filter`;

    this.saveDialogResetButtonLabel = $translate("ng.component.query-filters.save-query-reset-button-label")`Reset`;
    this.saveDialogCancelButtonLabel = $translate("ng.component.query-filters.save-query-cancel-button-label")`Cancel`;
    this.saveDialogSaveButtonLabel = $translate("ng.component.query-filters.save-query-save-button-label")`Save Filter`;
    this.saveDialogMessage = $translate("ng.component.query-filters.save-query-dialog-message")`Great news! Now you can save your filters for later use.\nPlease note, when you save the filter it will also apply the filter.`;
  }
}
